import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components/macro";
import IconButton from "../IconButton";

const TOOLBAR_Z_INDEX = 1000000002;
const TOOLBAR_GAP = 8;
const TOOLBAR_FALLBACK_HEIGHT = 40;

const FORMAT_ACTIONS = [
  {
    id: "bold",
    src: "/assets/editor-toolbar/Bold.svg",
    ariaLabel: "Bold",
    hoverTipText: "Bold",
    className: "bold",
    isActive: (editor: Editor) => editor.isActive("bold"),
    run: (editor: Editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    id: "italic",
    src: "/assets/editor-toolbar/italics.svg",
    ariaLabel: "Italic",
    hoverTipText: "Italic",
    className: "italic",
    isActive: (editor: Editor) => editor.isActive("italic"),
    run: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    id: "strikethrough",
    src: "/assets/editor-toolbar/strikethrough.svg",
    ariaLabel: "strikethrough",
    hoverTipText: "Strikethrough",
    className: "strike",
    isActive: (editor: Editor) => editor.isActive("strike"),
    run: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
  },
  {
    id: "bullets",
    src: "/assets/editor-toolbar/bullets.svg",
    ariaLabel: "Unordered List",
    hoverTipText: "Bullet List",
    className: "bullets",
    isActive: (editor: Editor) => editor.isActive("bulletList"),
    run: (editor: Editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: "paragraph",
    src: "/assets/editor-toolbar/Aa.svg",
    ariaLabel: "Paragraph",
    hoverTipText: "Paragraph",
    className: "paragraph",
    isActive: (editor: Editor) => ![1, 2, 3].some((level) => editor.isActive("heading", { level })),
    run: (editor: Editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    id: "h1",
    src: "/assets/editor-toolbar/h1.svg",
    ariaLabel: "Heading 1",
    hoverTipText: "Heading 1",
    className: "h1",
    isActive: (editor: Editor) => editor.isActive("heading", { level: 1 }),
    run: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: "h2",
    src: "/assets/editor-toolbar/h2.svg",
    ariaLabel: "Heading 2",
    hoverTipText: "Heading 2",
    className: "h2",
    isActive: (editor: Editor) => editor.isActive("heading", { level: 2 }),
    run: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: "h3",
    src: "/assets/editor-toolbar/h3.svg",
    ariaLabel: "Heading 3",
    hoverTipText: "Heading 3",
    className: "h3",
    isActive: (editor: Editor) => editor.isActive("heading", { level: 3 }),
    run: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
] as const;

function getEditorFallbackRect(editor: Editor) {
  const rect = editor.view.dom.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: Math.max(rect.width, 1),
    height: Math.max(rect.height, 1),
  };
}

function getSelectionViewportRect(editor: Editor) {
  const { from, to, empty } = editor.state.selection;
  if (empty) return null;

  try {
    const start = editor.view.coordsAtPos(from);
    const end = editor.view.coordsAtPos(to);
    const top = Math.min(start.top, end.top);
    const bottom = Math.max(start.bottom, end.bottom);
    const left = Math.min(start.left, end.left);
    const right = Math.max(start.right, end.right);
    return {
      top,
      left,
      width: Math.max(right - left, 1),
      height: Math.max(bottom - top, 1),
    };
  } catch {
    return getEditorFallbackRect(editor);
  }
}

interface StyleInstructionsFormatToolbarProps {
  editor: Editor | null;
}

export function StyleInstructionsFormatToolbar({ editor }: StyleInstructionsFormatToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isPointerSelecting, setIsPointerSelecting] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [viewportVersion, setViewportVersion] = useState(0);
  const [toolbarSize, setToolbarSize] = useState({ width: 0, height: TOOLBAR_FALLBACK_HEIGHT });

  const formatState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor) {
        return {
          empty: true,
          from: 0,
          to: 0,
          activeIds: [] as string[],
        };
      }

      return {
        empty: currentEditor.state.selection.empty,
        from: currentEditor.state.selection.from,
        to: currentEditor.state.selection.to,
        activeIds: FORMAT_ACTIONS.filter((action) => action.isActive(currentEditor)).map((action) => action.id),
      };
    },
  });

  useEffect(() => {
    setIsDismissed(false);
  }, [formatState.from, formatState.to]);

  useEffect(() => {
    if (!editor) return undefined;

    const onEditorPointerDown = () => {
      setIsDismissed(false);
      setIsPointerSelecting(true);
    };
    const onPointerUp = () => setIsPointerSelecting(false);
    const onViewportChange = () => setViewportVersion((version) => version + 1);
    const onDocumentPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (editor.view.dom.contains(target)) return;
      if (toolbarRef.current?.contains(target)) return;
      setIsDismissed(true);
    };

    editor.view.dom.addEventListener("mousedown", onEditorPointerDown);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    document.addEventListener("mousedown", onDocumentPointerDown);

    return () => {
      editor.view.dom.removeEventListener("mousedown", onEditorPointerDown);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
      document.removeEventListener("mousedown", onDocumentPointerDown);
    };
  }, [editor]);

  const shouldShow = Boolean(editor && !formatState.empty && !isPointerSelecting && !isDismissed);
  const selectionRect = useMemo(() => {
    if (!editor || !shouldShow) return null;
    return getSelectionViewportRect(editor);
    // viewportVersion is intentional: scroll/resize moves the selection box without a transaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, shouldShow, formatState, viewportVersion]);

  useLayoutEffect(() => {
    if (!toolbarRef.current) return;
    const { offsetWidth, offsetHeight } = toolbarRef.current;
    setToolbarSize({
      width: offsetWidth,
      height: offsetHeight || TOOLBAR_FALLBACK_HEIGHT,
    });
  }, [shouldShow, formatState.activeIds]);

  if (!editor || !shouldShow || !selectionRect) return null;

  const toolbarHeight = toolbarSize.height || TOOLBAR_FALLBACK_HEIGHT;
  const showBelow = selectionRect.top < toolbarHeight + TOOLBAR_GAP + 8;
  const top = showBelow
    ? selectionRect.top + selectionRect.height + TOOLBAR_GAP
    : Math.max(8, selectionRect.top - toolbarHeight - TOOLBAR_GAP);
  const centerX = selectionRect.left + selectionRect.width / 2;
  const halfWidth = (toolbarSize.width || 280) / 2;
  const left = Math.min(Math.max(centerX, 8 + halfWidth), window.innerWidth - 8 - halfWidth);

  return createPortal(
    <FormatToolbar
      ref={toolbarRef}
      role="toolbar"
      aria-label="Text formatting"
      style={{ top, left }}
      onMouseDown={(event) => event.preventDefault()}
    >
      {FORMAT_ACTIONS.map((action) => {
        const active = formatState.activeIds.includes(action.id);
        return (
          <IconButton
            key={action.id}
            src={action.src}
            size={32}
            iconSize={24}
            ariaLabel={action.ariaLabel}
            hoverTipText={action.hoverTipText}
            className={["toolbar-icon", action.className, active ? "active" : ""].filter(Boolean).join(" ")}
            aria-pressed={active}
            onClick={() => action.run(editor)}
          />
        );
      })}
    </FormatToolbar>,
    document.body,
  );
}

const FormatToolbar = styled.div`
  position: fixed;
  z-index: ${TOOLBAR_Z_INDEX};
  display: inline-flex;
  align-items: center;
  padding: 4px;
  transform: translateX(-50%);
  border-radius: 10px;
  background: var(--menu-color);
  box-shadow: var(--default-drop-shadow);
  color: var(--grey-2);
  user-select: none;

  .toolbar-icon {
    width: 32px;
    color: var(--grey-2);

    svg path {
      fill: var(--grey-2);
    }

    &.active {
      color: var(--grey-0);

      svg path {
        fill: var(--grey-0);
      }
    }
  }
`;
